'use client';
import { Box } from 'ui/components/box';
import { Form } from 'ui/components/form';
import { FormEvent, useEffect, useRef, useState } from 'react';
import { AuthProvider, useAuth } from 'ui/providers/auth-provider';
import { toast } from 'ui/components/toast';
import { SendContactEmailArgs, sendContactEmail } from '../_server-actions/send-contact-email';
import posthog from 'posthog-js';

export function ContactForm() {
  return (
    <AuthProvider>
      <ContactFormConnected />
    </AuthProvider>
  );
}

function ContactFormConnected() {
  const formRef = useRef<HTMLFormElement>(null);
  const { user } = useAuth();
  const [form, setForm] = useState({ firstName: '', lastName: '', email: user?.email ?? '', phone: '', message: '' });
  const [loading, setLoading] = useState(false);
  const isValid = form.firstName && form.lastName && form.email && form.phone && form.message;

  useEffect(() => {
    if (user && formRef.current) {
      formRef.current.email.value = user.email || '';

      if (user.displayName) {
        const [firstName, lastName] = user.displayName
          .trim()
          .split(' ')
          .map((name) => name.trim());
        formRef.current.firstName.value = firstName;
        formRef.current.lastName.value = lastName;
      }

      if (user.phoneNumber) {
        const phone = user.phoneNumber;

        formRef.current.phone.value = phone;
      }
    }

    if (formRef.current) {
      setForm({
        firstName: formRef.current.firstName.value,
        lastName: formRef.current.lastName.value,
        email: formRef.current.email.value,
        phone: formRef.current.phone.value,
        message: formRef.current.message.value,
      });
    }
  }, [user]);

  return (
    <Form.Root
      onChange={(e: FormEvent) => {
        const { name, value } = e.target as HTMLInputElement;

        setForm((prev) => ({ ...prev, [name]: value }));
      }}
      onSubmit={async (e: FormEvent) => {
        e.preventDefault();

        if (formRef.current) {
          setLoading(true);

          try {
            await sendContactEmail(form);

            posthog.capture('contact_form_submitted', form);

            formRef.current.message.value = '';
            setForm((prev) => ({ ...prev, message: '' }));
            setLoading(false);
            toast({ key: 'contact-form', title: 'Success', description: 'Your message has been sent!' });
          } catch (e) {
            console.error(e);
            setLoading(false);
            toast({ key: 'contact-form', title: 'Error', description: 'There was an error sending your message.' });
          }
        }
      }}
      ref={formRef}
    >
      <Box css={{ display: 'grid', gridTemplateColumns: ['1fr', '1fr 1fr'], gap: 4, maxWidth: '20rem' }}>
        <Form.ControlledInput label='First name' name='firstName' placeholder='John' required />
        <Form.ControlledInput label='Last name' name='lastName' placeholder='Doe' required />
        <Form.ControlledInput
          defaultValue={form.email}
          label='Email'
          name='email'
          placeholder='john-doe@gmail.com'
          required
          type='email'
        />
        <Form.ControlledInput
          label='Phone'
          name='phone'
          pattern='[0-9\-]*'
          placeholder='702-987-6543'
          required
          type='phone'
        />
        <Box css={{ gridColumn: '1/-1' }}>
          <Form.ControlledTextarea
            css={{ '& textarea': { height: '10rem' } }}
            label='Message'
            name='message'
            placeholder="I'm having trouble with my ESA letter..."
            required
          />
        </Box>
        <Box>
          <Form.SubmitButton disabled={!isValid || loading} variant='filled'>
            Submit
          </Form.SubmitButton>
        </Box>
      </Box>
    </Form.Root>
  );
}
